import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Button, Spinner } from "@nike/epic-react-ui";
import {
  selectAccessToken,
  selectOpenIDToken,
  selectEmail
} from "@nike/oauth-client";
import { requireLogin } from "../auth/LoginRequired";

/**
 * Base URL for the cloudred-slackapp API. Not part of the general apiUrl proxy
 * because slackapp has its own dedicated public API Gateway (Slack must be
 * able to reach it directly without Akamai).
 *
 * The prod URL is a TODO -- fill in after the first prod terraform apply.
 */
const SLACKAPP_API =
  window.location.origin.includes("cloudred.nike.com") ||
  window.location.origin.includes("cloudred-prod.nikecloud.com")
    ? "https://SLACKAPP-PROD-INVOKE-URL/prod"
    : "https://q44igx8ujh.execute-api.us-west-2.amazonaws.com/test";

const mapProps = state => ({
  accessToken: selectAccessToken(state),
  idToken: selectOpenIDToken(state),
  email: selectEmail(state)
});

const OwnershipApprovalPage = ({ accessToken, idToken, email, data }) => {
  const reqId = data && data.reqId;
  // The approval token is a URL *path* segment (not a query param) so that it
  // survives the Okta login redirect performed by @nike/oauth-client, which
  // strips query strings. The email link is
  // /ownership-approval/<reqId>/<approvalToken>.
  const token = (data && data.approvalToken) || "";

  const [request, setRequest] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null); // "APPROVED"/"REJECTED"/"FAILED"
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!reqId || !token) {
      setError("Missing request id or token in URL.");
      return;
    }
    fetch(
      `${SLACKAPP_API}/ownership-request/${reqId}?t=${encodeURIComponent(
        token
      )}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json"
        }
      }
    )
      .then(async res => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(body.detail || `HTTP ${res.status}`);
        }
        setRequest(body);
      })
      .catch(err => setError(err.message));
  }, [reqId, token, accessToken]);

  const submit = async decision => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(
        `${SLACKAPP_API}/ownership-request/${reqId}/${decision}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "X-Okta-Id-Token": idToken,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ t: token, okta_email: email, note })
        }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      setOutcome(body.status || decision.toUpperCase());
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div style={{ margin: "2rem", maxWidth: 700 }}>
        <h2>Ownership Change Approval</h2>
        <p style={{ color: "#c00" }}>Error: {error}</p>
      </div>
    );
  }

  if (!request) {
    return <Spinner title="Loading approval request..." />;
  }

  if (outcome) {
    return (
      <div style={{ margin: "2rem", maxWidth: 700 }}>
        <h2>Ownership Change Approval</h2>
        <p>
          Request <code>{request.req_id}</code> is now{" "}
          <strong style={{ color: outcome === "COMPLETED" ? "#0a0" : "#c60" }}>
            {outcome}
          </strong>
          .
        </p>
        {outcome === "COMPLETED" && (
          <p>
            Ownership of tagguid <code>{request.tagguid}</code> has been
            transferred.
          </p>
        )}
      </div>
    );
  }

  const notYou =
    email &&
    request.approver_email &&
    email.toLowerCase() !== request.approver_email;
  const expired = request.expires_at * 1000 < Date.now();

  return (
    <div style={{ margin: "2rem", maxWidth: 700 }}>
      <h2>Ownership Change Approval</h2>

      <table
        cellPadding="6"
        style={{ borderCollapse: "collapse", margin: "12px 0" }}
      >
        <tbody>
          <tr>
            <td style={{ background: "#f4f4f4" }}>
              <strong>Request ID</strong>
            </td>
            <td>
              <code>{request.req_id}</code>
            </td>
          </tr>
          <tr>
            <td style={{ background: "#f4f4f4" }}>
              <strong>Tagguid</strong>
            </td>
            <td>
              <code>{request.tagguid}</code>
            </td>
          </tr>
          <tr>
            <td style={{ background: "#f4f4f4" }}>
              <strong>Requester</strong>
            </td>
            <td>{request.requester_email}</td>
          </tr>
          <tr>
            <td style={{ background: "#f4f4f4" }}>
              <strong>Current owner</strong>
            </td>
            <td>{request.current_owner_email || "(unknown)"}</td>
          </tr>
          <tr>
            <td style={{ background: "#f4f4f4" }}>
              <strong>Proposed new owner</strong>
            </td>
            <td>
              <strong>{request.new_owner_email}</strong>
            </td>
          </tr>
          <tr>
            <td style={{ background: "#f4f4f4" }}>
              <strong>Approver (you)</strong>
            </td>
            <td>{request.approver_email}</td>
          </tr>
          <tr>
            <td style={{ background: "#f4f4f4" }}>
              <strong>Status</strong>
            </td>
            <td>{request.status}</td>
          </tr>
          <tr>
            <td style={{ background: "#f4f4f4" }}>
              <strong>Expires</strong>
            </td>
            <td>{new Date(request.expires_at * 1000).toLocaleString()}</td>
          </tr>
        </tbody>
      </table>

      {notYou && (
        <p style={{ color: "#c00" }}>
          You are signed in as <strong>{email}</strong>, but this request is
          addressed to <strong>{request.approver_email}</strong>. You will not
          be permitted to approve or reject it.
        </p>
      )}

      {expired && (
        <p style={{ color: "#c00" }}>
          This request has expired and can no longer be resolved.
        </p>
      )}

      <div style={{ marginTop: "1rem" }}>
        <label>
          Note (optional -- shown to the requester):
          <br />
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            rows="3"
            style={{ width: "100%", maxWidth: 700 }}
            disabled={submitting || notYou || expired}
          />
        </label>
      </div>

      <div style={{ marginTop: "1rem" }}>
        <Button
          onClick={() => submit("approve")}
          disabled={submitting || notYou || expired}
          style={{ marginRight: "0.5rem", background: "#0a7d24" }}
        >
          Approve
        </Button>
        <Button
          onClick={() => submit("reject")}
          disabled={submitting || notYou || expired}
          style={{ background: "#c00" }}
        >
          Reject
        </Button>
      </div>
    </div>
  );
};

export default connect(mapProps)(
  requireLogin("Ownership approval")(OwnershipApprovalPage)
);
